import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ListPlus, Loader2, RotateCcw, Save, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { accountsApi, type AccountDto } from '@/features/accounts/api'
import { boardsApi, type BoardDto } from '@/features/boards/api'
import MediaPicker from '../components/MediaPicker'
import { pinsApi, type MediaDto } from '../api'
import { PIN_STATUS_LABEL, TEXT_LIMITS } from '../constants'

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'

export default function PinEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()

  const [accounts, setAccounts] = useState<AccountDto[]>([])
  const [accountId, setAccountId] = useState('')
  const [boards, setBoards] = useState<BoardDto[]>([])
  const [boardId, setBoardId] = useState('')
  const [media, setMedia] = useState<MediaDto | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [link, setLink] = useState('')
  const [altText, setAltText] = useState('')
  const [status, setStatus] = useState('draft')
  const [lastError, setLastError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [queueBusy, setQueueBusy] = useState(false)
  const load = useCallback(async () => {
    try {
      const accountsRes = await accountsApi.list()
      setAccounts(accountsRes.accounts)
      if (!isNew) {
        const { pin } = await pinsApi.get(id!)
        setAccountId(pin.accountId)
        setBoardId(pin.boardId ?? '')
        setMedia(pin.media)
        setTitle(pin.title ?? '')
        setDescription(pin.description ?? '')
        setLink(pin.link ?? '')
        setAltText(pin.altText ?? '')
        setStatus(pin.status)
        setLastError(pin.lastErrorMessage)
      } else {
        setAccountId(accountsRes.accounts[0]?.id ?? '')
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t load data')
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    void load()
  }, [load])

  // Board ikut akun: daftarnya dimuat ulang tiap akun berganti, dan pilihan
  // yang tidak ada di akun baru dibuang supaya tidak mengirim board milik
  // akun lain (server menolaknya dengan INVALID_BOARD).
  useEffect(() => {
    if (!accountId) {
      setBoards([])
      return
    }
    let aktif = true
    boardsApi
      .list(accountId)
      .then((res) => {
        if (!aktif) return
        setBoards(res.boards)
        setBoardId((prev) => (res.boards.some((b) => b.id === prev) ? prev : ''))
      })
      .catch(() => aktif && setBoards([]))
    return () => {
      aktif = false
    }
  }, [accountId])

  /** Cerminan validatePostable() di server — dipakai untuk menonaktifkan tombol. */
  const problems: string[] = []
  if (!media) problems.push('media')
  if (!description.trim()) problems.push('description')
  if (!boardId) problems.push('board')

  const isDraft = status === 'draft' || status === 'cancelled'
  const isQueued = status === 'queued' || status === 'scheduled'
  const isFailed = status === 'failed'
  const locked = status === 'posted' || status === 'dispatched' || status === 'posting'

  async function save(): Promise<string> {
    const body = {
      accountId,
      mediaId: media?.id ?? null,
      boardId: boardId || null,
      title: title || null,
      description: description || null,
      link: link || null,
      altText: altText || null,
    }
    if (isNew) {
      const { pin } = await pinsApi.create(body)
      navigate(`/pins/${pin.id}`, { replace: true })
      return pin.id
    }
    await pinsApi.update(id!, body)
    return id!
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await save()
      toast.success(isNew ? 'Draft saved' : 'Changes saved')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t save')
    } finally {
      setSaving(false)
    }
  }

  /** Simpan dulu baru antrekan — supaya yang diantre adalah isi terbaru. */
  async function onEnqueue() {
    setQueueBusy(true)
    try {
      const pinId = await save()
      await pinsApi.enqueue(pinId)
      toast.success('Pin added to the queue')
      navigate('/pins')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t add to the queue')
      setQueueBusy(false)
    }
  }

  async function onPostNow() {
    toast('Post this pin now?', {
      description: 'The pin will be scheduled immediately; the account\'s minimum gap between posts still applies.',
      action: {
        label: 'Post',
        onClick: () => {
          setQueueBusy(true)
          pinsApi
            .postNow(id!)
            .then(() => {
              toast.success('Pin scheduled now — the agent will process it')
              navigate('/pins')
            })
            .catch((err: unknown) => {
              toast.error(err instanceof ApiError ? err.message : 'Couldn\'t post now')
              setQueueBusy(false)
            })
        },
      },
    })
  }

  async function onRetry() {
    setQueueBusy(true)
    try {
      await save()
      await pinsApi.retry(id!)
      toast.success('Pin retried — added back to the queue')
      navigate('/pins')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Couldn\'t retry')
      setQueueBusy(false)
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>

  if (accounts.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Add a Pinterest account first before creating a pin.
        </CardContent>
      </Card>
    )
  }

  const statusLabel = PIN_STATUS_LABEL[status] ?? PIN_STATUS_LABEL.draft

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">{isNew ? 'New pin' : 'Edit pin'}</h1>
          {!isNew && (
            <span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', statusLabel.className)}>
              {statusLabel.text}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant={isDraft ? 'outline' : 'default'} disabled={saving || locked}>
            {saving ? <Loader2 className="animate-spin" /> : <Save />}
            Save draft
          </Button>

          {isDraft && (
            <Button
              type="button"
              disabled={queueBusy || saving || problems.length > 0}
              title={
                problems.length ? `Complete first: ${problems.join(', ')}` : 'Add to queue'
              }
              onClick={() => void onEnqueue()}
            >
              {queueBusy ? <Loader2 className="animate-spin" /> : <ListPlus />}
              Save &amp; queue
            </Button>
          )}

          {isQueued && (
            <Button type="button" disabled={queueBusy} onClick={() => void onPostNow()}>
              {queueBusy ? <Loader2 className="animate-spin" /> : <Zap />}
              Post now
            </Button>
          )}

          {isFailed && (
            <Button
              type="button"
              disabled={queueBusy || problems.length > 0}
              title={problems.length ? `Complete first: ${problems.join(', ')}` : 'Try again'}
              onClick={() => void onRetry()}
            >
              {queueBusy ? <Loader2 className="animate-spin" /> : <RotateCcw />}
              Try again
            </Button>
          )}
        </div>
      </header>

      {isFailed && lastError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Last attempt failed: {lastError}
        </div>
      )}

      {locked && (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {status === 'posted'
            ? 'The pin is published and can\'t be edited from here.'
            : 'The agent is processing this pin — wait until it finishes before editing.'}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Media</CardTitle>
          </CardHeader>
          <CardContent>
            <MediaPicker value={media} onChange={setMedia} />
            <p className="mt-3 text-xs text-muted-foreground">
              Pinterest recommends a 2:3 ratio (e.g. 1000×1500 px).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Pin content</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="account">Pinterest account</Label>
                <select
                  id="account"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={!isNew}
                  className={SELECT_CLASS}
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                      {a.username ? ` (@${a.username})` : ''}
                    </option>
                  ))}
                </select>
                {!isNew && (
                  <p className="text-xs text-muted-foreground">
                    The account can't be changed after the pin is created.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="board">Destination board</Label>
                <select
                  id="board"
                  value={boardId}
                  onChange={(e) => setBoardId(e.target.value)}
                  disabled={locked || boards.length === 0}
                  className={SELECT_CLASS}
                >
                  <option value="">
                    {boards.length === 0 ? 'No boards yet' : 'Select board…'}
                  </option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                {boards.length === 0 ? (
                  <p className="text-xs text-amber-500">
                    <Link to="/boards" className="underline">
                      Sync boards
                    </Link>{' '}
                    first — the board is required before the pin can be queued.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Required to enter the queue.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="title">Title</Label>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {title.length}/{TEXT_LIMITS.TITLE}
                </span>
              </div>
              <Input
                id="title"
                maxLength={TEXT_LIMITS.TITLE}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label htmlFor="description">Description</Label>
                </div>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {description.length}/{TEXT_LIMITS.DESCRIPTION}
                </span>
              </div>
              <Textarea
                id="description"
                rows={5}
                maxLength={TEXT_LIMITS.DESCRIPTION}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="link">
                Destination link <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="link"
                type="url"
                placeholder="https://example.com/article"
                value={link}
                onChange={(e) => setLink(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                A pin without a link can still be posted as long as it has media and a description.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alt">
                Alt text <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="alt"
                maxLength={TEXT_LIMITS.ALT_TEXT}
                value={altText}
                onChange={(e) => setAltText(e.target.value)}
              />
            </div>

            {isDraft && problems.length > 0 && (
              <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                Can't be queued yet — complete first: {problems.join(', ')}.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </form>
  )
}
